import React from 'react';
import {
  Clock, Zap, Settings, TrendingUp, Activity, Target,
  Shield, Sun, Moon, CloudSun, Battery, BarChart3,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const RTCPowerOptimizationPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'RTC Power Optimization',
      title: (
        <>
          24/7 power optimization <em>for maximum performance</em>
        </>
      ),
      lede: 'Optimize your renewable energy assets around the clock with our intelligent power optimization platform. Maximize generation, minimize losses, and ensure peak performance 24/7.',
      image: '/assets/img/general/solar-8244680_1280.jpg',
      imageAlt: 'Solar plant generating at scale',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '24/7', label: 'Continuous optimization' },
        { value: '20%', label: 'Output increase' },
        { value: 'Real-time', label: 'Adjustments' },
        { value: '100%', label: 'Automated' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'A complete optimization solution',
      lede: 'Our RTC power optimization platform provides comprehensive tools to maximize your renewable energy generation around the clock.',
      body: [
        'Generation, storage and grid delivery are tuned continuously against weather, demand and grid conditions — with every adjustment logged, so the operating decision behind each hour of output stays auditable.',
      ],
      checklist: [
        '24/7 real-time power optimization and monitoring',
        'Automated control systems for maximum efficiency',
        'Weather-adaptive optimization algorithms',
        'Grid integration and power quality management',
        'Load forecasting and demand response',
        'Dynamic reactive power control',
        'Automated curtailment management',
        'Performance tracking and reporting',
      ],
      image: '/assets/img/general/solar-2376233_1280.jpg',
      imageAlt: 'Solar array at dusk',
      imageCaption: 'Continuous power optimization ensuring maximum performance at all times',
    }}
    capabilities={{
      eyebrow: 'Optimization features',
      title: 'Intelligent power optimization',
      lede: 'Advanced features designed for continuous power optimization.',
      items: [
        {
          icon: Clock,
          title: '24/7 optimization',
          description: 'Round-the-clock monitoring and optimization ensuring peak performance at all times.',
        },
        {
          icon: Zap,
          title: 'Real-time adjustments',
          description: 'Dynamic power optimization with instant adjustments based on real-time conditions.',
        },
        {
          icon: Settings,
          title: 'Automated control',
          description: 'Intelligent automation that optimizes power generation without manual intervention.',
        },
        {
          icon: Target,
          title: 'Maximum efficiency',
          description: 'Achieve peak efficiency through continuous optimization and performance tuning.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Optimization areas',
      title: 'Comprehensive optimization coverage',
      lede: 'Optimize power generation across all conditions and timeframes.',
      items: [
        {
          icon: Sun,
          title: 'Day-time optimization',
          description: 'Maximize power generation during peak sunlight hours with intelligent tracking.',
        },
        {
          icon: Moon,
          title: 'Night-time management',
          description: 'Optimize energy storage and grid integration during night hours.',
        },
        {
          icon: CloudSun,
          title: 'Weather adaptation',
          description: 'Dynamic adjustment based on weather conditions for optimal performance.',
        },
        {
          icon: Activity,
          title: 'Grid synchronization',
          description: 'Seamless grid integration with real-time power quality optimization.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Key benefits',
      title: 'Maximize your energy output',
      lede: 'Achieve significant improvements through continuous optimization.',
      items: [
        {
          icon: TrendingUp,
          title: 'Increase output',
          description: 'Boost power generation by 10-20% through continuous optimization strategies.',
        },
        {
          icon: Shield,
          title: 'Reduce losses',
          description: 'Minimize power losses and maximize energy delivery to the grid.',
        },
        {
          icon: Battery,
          title: 'Grid stability',
          description: 'Maintain stable grid connection with optimized power delivery.',
        },
        {
          icon: BarChart3,
          title: 'Revenue growth',
          description: 'Increase revenue through optimized power generation and grid integration.',
        },
      ],
    }}
    cta={{
      title: 'Ready for 24/7 optimization?',
      body: 'Start maximizing your power generation with round-the-clock optimization.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default RTCPowerOptimizationPage;
